import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Box, ButtonBase, Link } from '@/shared/ui';

import { spacing } from '@/config/ui';
import type { RestrictedAccessPageContent } from '@/entities/auth-access';
import Button from '@/shared/ui/primitives/Button';
import { SupportContact } from '@/shared/ui/primitives/SupportContact';
import Typography from '@/shared/ui/typography/Text';

type RestrictedAccessContentProps = {
  content: RestrictedAccessPageContent;
  onPrimaryActionClick: () => void;
};

export const RestrictedAccessContent = ({
  content,
  onPrimaryActionClick
}: RestrictedAccessContentProps) => (
  <>
    <Typography
      size={32}
      sx={{
        fontWeight: 700,
        lineHeight: 1.15,
        mb: spacing[4]
      }}
    >
      {content.title}
    </Typography>

    <Typography
      size={20}
      sx={{
        color: 'gx.subtext',
        mb: spacing[4]
      }}
    >
      {content.subtitle}
    </Typography>

    <Button
      fullWidth
      size='large'
      sx={{
        width: '100%',
        maxWidth: '480px',
        mb: spacing[4]
      }}
      variant='contained'
      onClick={onPrimaryActionClick}
    >
      {content.primaryActionLabel}
    </Button>

    {content.secondaryLinkLabel && content.secondaryLinkHref && (
      <Link
        href={content.secondaryLinkHref}
        rel='noopener noreferrer'
        sx={{
          display: 'inline-flex',
          textUnderlineOffset: '3px',
          mb: spacing[5],
          color: 'gx.subtext'
        }}
        target='_blank'
        underline='hover'
      >
        {content.secondaryLinkLabel}
      </Link>
    )}

    <SupportContact sx={{ color: 'gx.subtext', fontSize: '1rem' }} />
  </>
);

type RestrictedAccessPageProps = {
  content: RestrictedAccessPageContent;
  onBackClick: () => void;
  onPrimaryActionClick: () => void;
};

const RestrictedAccessPage = ({
  content,
  onBackClick,
  onPrimaryActionClick
}: RestrictedAccessPageProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '100%',
      maxWidth: '720px',
      mx: 'auto',
      mt: {
        xs: 1,
        md: 2
      }
    }}
  >
    <ButtonBase
      sx={{
        color: 'common.white',
        mb: spacing[6]
      }}
      onClick={onBackClick}
    >
      <ChevronLeft sx={{ fontSize: '1rem', mr: 0.5 }} />
      <Typography
        size={16}
        sx={{ fontWeight: 500 }}
      >
        {content.backLabel}
      </Typography>
    </ButtonBase>

    <RestrictedAccessContent
      content={content}
      onPrimaryActionClick={onPrimaryActionClick}
    />
  </Box>
);

export default RestrictedAccessPage;
